import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { environment } from "../config/environment";

/**
 * Application Insights client. Initialised once at app startup via
 * `initAppInsights()`. The wrappers below (`trackEvent`, `trackException`)
 * are safe to call before init or in builds without an instrumentation key
 * — they no-op rather than throwing, so callers don't need a feature check.
 */
let appInsights: ApplicationInsights | null = null;

/**
 * The signed-in user's email, set by `setTelemetryUser` once sign-in completes. Telemetry sent
 * before then carries only the SDK's anonymous id.
 */
let signedInUserId: string | null = null;

/**
 * Initialise the App Insights SDK. Called once from index.tsx at app start.
 * Becomes a no-op when no instrumentation key is configured — typically
 * local dev builds without a CI-injected key.
 *
 * Auto-tracking enabled: page-visit time, unhandled promise rejections,
 * fetch/XHR calls. Every event is stamped with the addin version + role so
 * analytics can be sliced per release.
 *
 * Page views are NOT auto-tracked — `enableAutoRouteTracking` hooks the
 * History API, which `MemoryRouter` never touches. Instead, the
 * `useTrackPageViews` hook (mounted inside the Router) emits explicit
 * page views on every route change.
 */
export function initAppInsights(): void {
  const iKey = environment.appInsightsInstrumentationKey;
  if (!iKey) {
    console.info("[app-insights] no instrumentation key configured — telemetry disabled");
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: iKey,
      autoTrackPageVisitTime: true,
      enableUnhandledPromiseRejectionTracking: true,
    },
  });
  appInsights.loadAppInsights();

  // Tag every event with version + role so the addin's telemetry is easy to
  // separate from the API's in shared queries. `ai.cloud.role` is the
  // standard App Insights tag for "which component is reporting this."
  appInsights.addTelemetryInitializer((envelope) => {
    envelope.tags = envelope.tags ?? {};
    envelope.tags["ai.cloud.role"] = "wfi-addin";
    envelope.tags["ai.application.ver"] = environment.version;

    // Users are counted by email, not the SDK's anonymous id: that id lives only in the `ai_user`
    // cookie and is replaced whenever the cookie is missing. It is kept as a property so how often
    // that happens can still be measured. These tags override the SDK's own when the envelope is built.
    if (signedInUserId) {
      const anonymousId = envelope.ext?.user?.id;
      if (anonymousId) {
        envelope.data = envelope.data ?? {};
        envelope.data.anonymousUserId = anonymousId;
      }
      envelope.tags["ai.user.id"] = signedInUserId;
      envelope.tags["ai.user.authUserId"] = signedInUserId;
    }
  });

  // First page view is emitted by useTrackPageViews on its initial render.
  console.info("[app-insights] telemetry initialised");
}

/**
 * Identify all telemetry from here on by the signed-in user's email (lower-cased) instead of the
 * SDK's anonymous id. Called once sign-in completes; safe to call before init.
 */
export function setTelemetryUser(email: string | null | undefined): void {
  signedInUserId = email?.trim().toLowerCase() || null;
}

/**
 * Track a custom event. Safe to call before init or in builds with telemetry
 * disabled — silently no-ops. Use for "user did X" signals (button clicks,
 * feature usage) that you want to count or slice in queries.
 */
export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  appInsights?.trackEvent({ name }, properties);
}

/**
 * Track a caught exception. Safe to call before init. Unhandled exceptions
 * are already captured automatically; use this for caught errors you still
 * want to record (e.g. an API call that failed but you handled the UI).
 */
export function trackException(error: Error, properties?: Record<string, unknown>): void {
  appInsights?.trackException({ exception: error, properties });
}

/**
 * Track a page view with an explicit name. Wired up by the `useTrackPageViews`
 * hook on every react-router location change — `MemoryRouter` doesn't fire
 * History API events, so the SDK's `enableAutoRouteTracking` would never see
 * route changes. Safe to call before init (no-ops).
 */
export function trackPageView(name: string, properties?: Record<string, unknown>): void {
  appInsights?.trackPageView({ name, uri: name, properties });
}
